import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, Calendar, MapPin, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5 } }),
};

export default function TicketsPage() {
  const [events, setEvents] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/events`),
      axios.get(`${API}/ticket-types`),
    ])
      .then(([ev, tt]) => {
        setEvents(ev.data || []);
        setTiers(tt.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Jamaica-local date (UTC-5) so a late-night-local event isn't filtered
  // out a few hours early just because UTC has already ticked over.
  const todayJa = new Date(Date.now() - 5 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const upcoming = events
    .filter((e) => e.event_date && e.event_date >= todayJa)
    .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));

  const minPrice = tiers.length
    ? Math.min(...tiers.map((t) => t.price || 0))
    : null;

  return (
    <div data-testid="tickets-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="mb-14">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">
              ✦ Tickets
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Secure your <span className="gold-gradient-text italic">spot.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/55 font-body font-light text-base sm:text-lg max-w-2xl">
              Upcoming events and experiences with tickets on sale. Browse, pick a tier, and check out in under two minutes.
            </motion.p>
          </motion.div>

          {/* Tier reference strip — shows price tiers at a glance */}
          {tiers.length > 0 && (
            <motion.div initial="hidden" animate="visible" variants={{ hidden: {}, visible: {} }} className="mb-12 flex flex-wrap gap-3">
              {tiers.map((t, i) => (
                <motion.div
                  key={t.id}
                  variants={fadeUp}
                  custom={3 + i}
                  data-testid={`ticket-tier-pill-${t.id}`}
                  className="inline-flex items-center gap-2 border border-gold/25 bg-gold/5 px-4 py-2"
                >
                  <Sparkles size={12} className="text-gold" />
                  <span className="text-white font-body text-xs">{t.name}</span>
                  <span className="text-gold font-body text-xs">${t.price}</span>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Upcoming events with tickets */}
          {loading ? (
            <div data-testid="tickets-loading" className="text-gold animate-pulse text-sm font-body">
              Loading…
            </div>
          ) : upcoming.length === 0 ? (
            <div data-testid="tickets-empty" className="glass-card p-16 text-center">
              <Ticket size={48} className="text-gold/30 mx-auto mb-4" />
              <h3 className="text-white font-heading text-xl mb-2">No upcoming events</h3>
              <p className="text-white/40 font-body text-sm mb-6 max-w-md mx-auto">
                We don't have any ticketed events on sale right now. Subscribe to be notified when the next one drops.
              </p>
              <Link
                to="/subscribe"
                data-testid="tickets-subscribe-cta"
                className="inline-flex items-center gap-2 border border-gold/40 hover:border-gold text-gold text-xs font-body uppercase tracking-wider px-5 py-2.5 transition-colors"
              >
                Get notified <ArrowRight size={13} />
              </Link>
            </div>
          ) : (
            <motion.div initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tickets-grid">
              {upcoming.map((ev, i) => (
                <motion.div key={ev.id} variants={fadeUp} custom={i}>
                  <Link
                    to={`/events/${ev.id}#tickets`}
                    data-testid={`ticket-event-card-${ev.id}`}
                    className="glass-card overflow-hidden group block hover:border-gold/40 transition-all"
                  >
                    {ev.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden bg-luxury-surface relative">
                        <img
                          src={ev.cover_image}
                          alt={ev.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 px-3 py-1 bg-black/65 backdrop-blur-sm text-gold text-[10px] font-body uppercase tracking-wider">
                          {ev.event_type}
                        </div>
                        {minPrice !== null && (
                          <div className="absolute top-3 right-3 px-3 py-1 bg-gold text-luxury-black text-[10px] font-body uppercase tracking-wider">
                            From ${minPrice}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-white font-heading text-lg group-hover:text-gold transition-colors mb-3">
                        {ev.title}
                      </h3>
                      <div className="flex items-center gap-4 text-white/40 text-xs font-body mb-5">
                        {ev.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} /> {ev.event_date}
                          </span>
                        )}
                        {ev.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} /> {ev.location}
                          </span>
                        )}
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                        Buy tickets <ArrowRight size={13} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
